import type { QueryObserverOptions } from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { omit } from 'lodash-es';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { frameApi } from '@/api';
import { QUERY_KEY_CONST, QUERY_META_CONST, TASK_CONST } from '@/constant';
import usePaginationSearchParams from '@/hook/tanstackQuery/usePaginationSearchParams';
import type {
  FrameApi,
  FrameData,
  FrameSearchParams,
  Modeling,
  User,
} from '@/type';
import { ERROR_CODE } from '@/util';

import { useTypedSearchParams } from '../searchParams';

export const useFrameCountQuery = (
  param: Partial<{
    workerID: string[];
    reviewerID: string[];
    userID: User['id'];
  }> & {
    workState?: FrameData['workState'][];
  } & Pick<FrameData, 'taskID'>,
) => {
  const { get, getAll } = useTypedSearchParams<FrameSearchParams>();

  const taskID = param?.taskID ?? get('frameTaskId');
  const workerID = param?.workerID ?? getAll('frameWorkerId');
  const workState = param?.workState ?? getAll('frameWorkState');
  const reviewerID = param?.reviewerID ?? getAll('frameReviewerId');

  const result = useQuery({
    queryKey: QUERY_KEY_CONST.FRAME_KEYS.getCount({
      taskID,
      reviewerID: reviewerID.length ? reviewerID : null,
      workerID,
      workState: workState.length
        ? (workState as FrameData['workState'][])
        : undefined,
    }),
    queryFn: () => {
      const query = {
        pTaskID: taskID,
        workerID: param?.userID
          ? [param.userID]
          : workerID.length
            ? workerID
            : null,
      } as FrameApi.GetCount.Request;

      reviewerID && (query.reviewerID = reviewerID);
      workState.length &&
        (query.workState = workState as FrameData['workState'][]);

      Object.entries(query).forEach(([key, value]) => {
        if (value == null) delete query[key as keyof typeof query];
      });

      return frameApi.getCount(query).then(res => res.data.resultData);
    },
  });

  return result;
};

export const useFrameDetailQuery = (
  param: Pick<FrameData, 'frameSeq' | 'taskID'>,
) => {
  const queryClient = useQueryClient();
  const { frameSeq, taskID } = param;
  const result = useQuery({
    queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail({ taskID, frameSeq }),
    queryFn: () => {
      if (frameSeq < 1 || isNaN(frameSeq)) return Promise.resolve(null);

      return frameApi
        .getDetail({
          pFrameSeq: frameSeq,
          pTaskID: taskID,
        })
        .then(res => res.data.resultData)
        .finally(() => {
          queryClient.invalidateQueries({
            queryKey: QUERY_KEY_CONST.FRAME_KEYS.getNeighbor(),
          });
        });
    },
    enabled: !!frameSeq && !isNaN(frameSeq),
    staleTime: 5000, // 5 seconds
  });

  return result;
};

export const useFrameListQuery = (
  param?: Partial<
    Pick<
      FrameApi.GetList.Request,
      | 'cursor'
      | 'hasWorkData'
      | 'hasViewOnly'
      | 'limit'
      | 'offset'
      | 'reviewerID'
      | 'workerID'
      | 'workState'
      | 'frameSeq'
    > & {
      taskID: FrameApi.GetList.Request['pTaskID'];
      userID: User['id'];
    }
  >,
  option?: Pick<
    QueryObserverOptions<
      {
        frames: FrameData[];
        cursor: string;
      },
      Error,
      {
        frames: FrameData[];
        cursor: string;
      }
    >,
    'enabled' | 'gcTime' | 'initialData' | 'retry' | 'retryDelay' | 'staleTime'
  >,
) => {
  const { get, getAll } = useTypedSearchParams<{
    frameTaskId: string;
    frameWorkerId: string[];
    frameWorkState: string[];
    frameReviewerId: string[];
  }>();

  const paginationSearchParams = usePaginationSearchParams('frame');
  const taskId = param?.taskID ?? get('frameTaskId');
  const workerID = param?.workerID ?? getAll('frameWorkerId');
  const workState = param?.workState ?? getAll('frameWorkState');
  const reviewerID = param?.reviewerID ?? getAll('frameReviewerId');

  const query: FrameApi.GetList.Request = {
    ...paginationSearchParams,
    cursor: param?.cursor ?? paginationSearchParams.cursor,
    hasWorkData: param?.hasWorkData,
    limit: param?.limit ?? paginationSearchParams.limit,
    offset: param?.offset ?? paginationSearchParams.offset,
    pTaskID: Number(taskId!),
    workerID: param?.userID
      ? [param.userID]
      : workerID.length
        ? workerID
        : null,
    workState: workState ? (workState as FrameData['workState'][]) : null,
    frameSeq: param?.frameSeq,
    reviewerID: reviewerID ? reviewerID : null,
  };

  const enabled = (option?.enabled ?? true) && !!taskId;

  Object.entries(query).forEach(([key, value]) => {
    if (value == null) delete query[key as keyof typeof param];
  });

  return useQuery({
    queryFn: () =>
      frameApi.getList({ ...query }).then(res => ({
        frames: res.data.resultData,
        cursor: res.data.cursor,
      })),
    queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(query),
    ...option,
    enabled,
  });
};

export const useInfiniteFrameListQuery = (
  param: Partial<
    Pick<
      FrameApi.GetList.Request,
      | 'hasWorkData'
      | 'hasViewOnly'
      | 'reviewerID'
      | 'workerID'
      | 'workState'
      | 'limit'
      | 'frameSeq'
    >
  > & {
    taskID: FrameApi.GetList.Request['pTaskID'];
  },
) => {
  const { get, getAll } = useTypedSearchParams<FrameSearchParams>();

  const limit =
    param.limit ?? (get('frameLimit') ? Number(get('frameLimit')) : 50);
  const keyword = get('frameKeyword');
  const workerID = getAll('frameWorkerId');
  const reviewerID = getAll('frameReviewerId');
  const workState = getAll('frameWorkState');

  const listParam: FrameApi.GetList.Request = {
    limit,
    keyword,
    pTaskID: param.taskID,
    workerID: workerID.length ? workerID : null,
    reviewerID: reviewerID.length ? reviewerID : null,
    workState: workState.length
      ? (workState as FrameData['workState'][])
      : null,
    ...omit(param, 'taskID'),
  };

  Object.entries(listParam).forEach(([key, value]) => {
    if (value === null) delete listParam[key as keyof typeof listParam];
  });

  return useInfiniteQuery({
    queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(listParam),
    queryFn: ({ pageParam }) =>
      frameApi.getList({ ...listParam, cursor: pageParam }),
    initialPageParam: '',
    getNextPageParam: nextPage => nextPage.data.cursor,
    getPreviousPageParam: prevPage => prevPage.data.cursor,
  });
};

export const useUpdateFrameWorkStateMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const result = useMutation({
    mutationFn: (
      param: Pick<FrameData, 'frameSeq' | 'taskID' | 'workState'> & {
        currentWorkState?: FrameData['workState'];
      },
    ) => {
      const { currentWorkState, frameSeq, taskID, workState } = param;

      if (currentWorkState === workState)
        return Promise.reject(
          new Error(t('frame.updateWorkStateSameWorkStateError.transistor')),
        );

      return frameApi.updateWorkState({
        pFrameSeq: frameSeq,
        pTaskID: taskID,
        workState,
      });
    },
    onSuccess(_, { frameSeq, taskID }) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail({ frameSeq, taskID }),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getHistoryList(),
      });
    },
    onError: error => {
      if ((error as any).data.resultCode === ERROR_CODE['1412']) {
        // do nothing
        return;
      }
    },
    meta: {
      [QUERY_META_CONST.META_KEY.OVERRIDE_GLOBAL_ERROR_HANDLER]: true,
    },
  });

  return result;
};

export const useFrameSubmitQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (param: Omit<FrameApi.UpdateWorkState.Request, 'workState'>) =>
      frameApi.submit(param),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getHistoryList(),
      });
    },
  });
};

export const useNewLabelInFrame = (
  param: Pick<
    FrameApi.UpsertLabel.Request,
    'frameSeq' | 'projectID' | 'taskID'
  >,
) => {
  const { frameSeq, projectID, taskID } = param;
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationKey: ['frameApi.upsertLabel', frameSeq, projectID, taskID],
    /**
     * @description
     * frameSeq 를 강제로 받아서 갱신해야 하는 필요가 있어서 추가함
     */
    mutationFn: ({
      frameSeq: _frameSeq,
      workData,
      workState,
      reviewComment,
      reviewData,
    }: SelectivePartial<
      Pick<
        FrameApi.UpsertLabel.Request,
        'frameSeq' | 'workData' | 'workState' | 'reviewData' | 'reviewComment'
      >,
      'frameSeq'
    >) => {
      return frameApi.upsertLabel({
        pFrameSeq: _frameSeq ?? frameSeq,
        pTaskID: taskID,
        frameSeq: _frameSeq ?? frameSeq,
        projectID,
        taskID,
        workData,
        workState: workState ?? 'Working',
        reviewComment,
        reviewData,
      });
    },
    onSuccess(res) {
      queryClient.setQueryData(
        QUERY_KEY_CONST.FRAME_KEYS.getDetail({ taskID, frameSeq }),
        res.data.resultData,
      );
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.TRACK_KEYS.getTrackByFrameSeq(),
      });
    },
  });

  return result;
};

export const useResetFrame = (
  param: Pick<
    FrameApi.UpsertLabel.Request,
    'frameSeq' | 'projectID' | 'taskID'
  >,
) => {
  const { frameSeq, taskID } = param;
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationFn: () => {
      return frameApi.updateWorkState({
        pFrameSeq: frameSeq,
        pTaskID: taskID,
        workState: TASK_CONST.TASK_FRAME_WORKSTATE.PREWORK,
      });
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail({ taskID, frameSeq }),
      });
    },
  });

  return result;
};

export const useReviewLabelInFrame = (
  param: Pick<FrameData, 'frameSeq' | 'taskID'>,
) => {
  const { frameSeq, taskID } = param;
  const queryClient = useQueryClient();
  const result = useMutation({
    mutationKey: ['frameApi.review', frameSeq, taskID],
    mutationFn: ({
      reviewComment,
      reviewData,
      workData,
    }: Partial<
      Pick<FrameData, 'reviewComment' | 'reviewData' | 'workData'>
    >) => {
      return frameApi.review({
        pFrameSeq: frameSeq,
        pTaskID: taskID,
        frameSeq,
        reviewComment,
        reviewData,
        taskID,
        workData,
      });
    },
  });

  const accept = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (
      param: Partial<
        Pick<FrameData, 'reviewComment' | 'reviewData' | 'workData'>
      >,
    ) =>
      result
        .mutateAsync(param)
        .then(() =>
          frameApi.accept({
            pFrameSeq: frameSeq,
            pTaskID: taskID,
          }),
        )
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail({
              taskID,
              frameSeq,
            }),
          });
        }),
    [frameSeq, taskID],
  );

  const reject = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (
      param: Partial<
        Pick<FrameData, 'reviewComment' | 'reviewData' | 'workData'>
      >,
    ) =>
      result
        .mutateAsync(param)
        .then(() =>
          frameApi.reject({
            pFrameSeq: frameSeq,
            pTaskID: taskID,
          }),
        )
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail({
              taskID,
              frameSeq,
            }),
          });
          queryClient.invalidateQueries({
            queryKey: QUERY_KEY_CONST.FRAME_KEYS.getHistoryList(),
          });
        }),
    [frameSeq, taskID],
  );

  return {
    accept,
    reject,
  };
};

export const useFrameNeighborQuery = (
  param: FrameApi.GetNeighbor.Request,
  option?: Pick<
    QueryObserverOptions<
      {
        prev: Nullish<FrameData>;
        next: Nullish<FrameData>;
      },
      Error,
      {
        prev: Nullish<FrameData>;
        next: Nullish<FrameData>;
      }
    >,
    'enabled' | 'gcTime' | 'initialData' | 'retry' | 'retryDelay' | 'staleTime'
  >,
) => {
  const { frameSeq, taskID } = param;

  return useQuery({
    queryFn: () =>
      frameApi.getNeighbor(param).then(res => {
        const list = res.data.resultData;
        const result = { prev: null, next: null } as {
          [K in 'prev' | 'next']: Nullish<FrameData>;
        };

        list.forEach(frame => {
          const key = frame.frameSeq > frameSeq ? 'next' : 'prev';

          result[key] = frame;
        });

        return result;
      }),
    queryKey: QUERY_KEY_CONST.FRAME_KEYS.getNeighbor(param),
    ...option,
    enabled: (option?.enabled ?? true) && !!taskID && !!frameSeq,
  });
};

export const useSubmitAllFrameQuery = () => {
  return useMutation({
    mutationFn: (
      props: Omit<FrameApi.UpdateFrames.Request, 'frameWorkState' | 'frameSeq'>,
    ) =>
      frameApi.updateFrames({
        ...props,
        frameWorkState: TASK_CONST.TASK_FRAME_WORKSTATE.REVIEW,
      }),
  });
};

export const useAcceptAllFrameQuery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      props: Omit<FrameApi.UpdateFrames.Request, 'frameWorkState' | 'frameSeq'>,
    ) =>
      frameApi.updateFrames({
        ...props,
        frameWorkState: TASK_CONST.TASK_FRAME_WORKSTATE.DONE,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getCount(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getNeighbor(),
      });
    },
  });
};

export const useRequestFramePreAnnotationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      modelingID,
      useSession,
      ...payload
    }: Omit<FrameApi.PreAnnotation.Request, 'modelingID'> & {
      modelingID: Modeling['id'];
    }) => {
      return frameApi.requestFramePreAnnotation({
        modelingID: `${modelingID}`,
        useSession,
        ...payload,
      });
    },
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getDetail({
          frameSeq: res.data.resultData.frameSeq,
          taskID: res.data.resultData.taskID,
        }),
      });
    },
  });
};

export const useRequestSemiAnnotateFrameQuery = () => {
  return useMutation({
    mutationFn: ({
      modelingID,
      ...payload
    }: Omit<FrameApi.SemiAnnotation.Request, 'modelingID'> & {
      modelingID: Modeling['id'];
    }) =>
      frameApi.requestSemiAnnotation({
        ...payload,
        modelingID: `${modelingID}`,
      }),
  });
};

export const useChangeReviewerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      param: Omit<FrameApi.ChangeReviewer.Request, 'frameWorkState'>,
    ) => frameApi.changeReviewer(param),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(),
      });
    },
  });
};

export const useFrameHistoryListQuery = (
  param: FrameApi.GetHistoryList.Request,
  enabled = true,
) => {
  param.limit ??= 10;

  return useQuery({
    queryKey: QUERY_KEY_CONST.FRAME_KEYS.getHistoryList(param),
    queryFn: () =>
      frameApi.getHistoryList(param).then(res => res.data.resultData),
    enabled: enabled && param.frameSeq != null && param.taskID != null,
    staleTime: 3000,
  });
};
